import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  LabVisitsService,
  type LabVisit,
  type LabVisitCreate,
  type LabVisitRequisition,
  type LabVisitRequisitionBiometric,
  type LabVisitUpdate,
} from "@/lib/api_client";
import { getAuthHeaders, impersonateHeaders } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fetchAll, fetchAllByIds } from "./fetchAll";

export const useLabVisitsByUser = (authUserId: string | null) =>
  useQuery({
    queryKey: ["lab-visits", "user", authUserId],
    queryFn: async () => {
      const res = await LabVisitsService.getApiLabVisits({
        headers: getAuthHeaders(),
        query: {
          "filter[authUserId]": authUserId!,
          orderBy: "createdAt",
          order: "desc",
          limit: 100,
          offset: 0,
        },
      });
      return (res.data ?? []) as unknown as LabVisit[];
    },
    enabled: !!authUserId,
    staleTime: 60_000,
  });

/**
 * Batch fetch lab visits by ID set. Used to resolve authUserId when iterating
 * requisitions.
 *
 * Batched, because the id set grows with the dataset: the rekvirering page
 * feeds it every visit referenced by a ready, recently-sent or snail-mail
 * requisition, and past ~430 ids the `in:` filter pushed the request over the
 * API's 16 KB limit. The response to that is a 431 with no CORS headers, so
 * the browser reported an opaque network error and the old `res.data ?? []`
 * turned it into an empty — but successful — result. Every row on the page
 * resolves its visit through this map, so the whole table silently emptied
 * itself a moment after it had rendered. Errors now propagate, and
 * `keepPreviousData` keeps the table populated while the id set settles
 * instead of blanking it on every key change.
 */
export const useLabVisitsByIds = (ids: string[]) =>
  useQuery({
    queryKey: ["lab-visits", "byIds", [...ids].sort()],
    queryFn: () =>
      fetchAllByIds<LabVisit>([...ids].sort(), (batch, opts) =>
        LabVisitsService.getApiLabVisits({
          ...opts,
          query: { ...opts.query, "filter[id]": `in:${batch.join(",")}` },
        }),
      ),
    enabled: ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useGenerateAssessments = () => {
  const qc = useQueryClient();
  const { impersonateToken } = useAuth();
  return useMutation({
    mutationFn: async ({ labVisitId, pipeline, examples, model, regenerate }: {
      labVisitId: string;
      pipeline: "v1" | "v2";
      examples: "none" | "retrieved";
      model: "gemini" | "qwen" | "gemma" | "deepseek-pro" | "deepseek-flash" | "gemini-flash";
      regenerate?: boolean;
    }) => {
      let headers = getAuthHeaders();
      if (regenerate) {
        if (!impersonateToken) {
          throw new Error("Åpne pasienten før du regenererer vurderinger");
        }
        headers = impersonateHeaders(impersonateToken);
      }
      const res =
        await LabVisitsService.postApiAdminLabVisitsByIdGenerateAssessments({
          path: { id: labVisitId },
          body: { pipeline, examples, model, regenerate },
          headers,
        });
      if (!res.data) {
        const error: unknown = res.error;
        throw new Error(
          error !== null &&
          typeof error === "object" &&
          "message" in error &&
          typeof error.message === "string"
            ? error.message
            : "Kunne ikke starte generering",
        );
      }
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessments"] }),
  });
};

export const useUpdateLabVisit = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: LabVisitUpdate }) => {
      const res = await LabVisitsService.putApiLabVisitsById({
        path: { id },
        body: data,
        headers: getAuthHeaders(),
      });
      // The 409 body is `{ error: "<why>" }` — a lifecycle guard explaining
      // what has to happen first (e.g. unsent requisitions before
      // `requisitioned`). Carry it to the caller instead of a generic string,
      // or the UI can only say "something went wrong".
      if (!res.data) {
        throw new Error(
          (res.error as { error?: string } | undefined)?.error ??
            "Failed to update lab visit",
        );
      }
      return res.data as unknown as LabVisit;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-visits"] });
      qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] });
      qc.invalidateQueries({ queryKey: ["users-needing-action"] });
    },
  });
};

export const useCreateLabVisit = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: LabVisitCreate) => {
      const res = await LabVisitsService.postApiLabVisits({
        body: data,
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to create lab visit");
      return res.data as unknown as LabVisit;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-visits"] });
      qc.invalidateQueries({ queryKey: ["impersonated-lab-visits"] });
      qc.invalidateQueries({ queryKey: ["crm-users"] });
      qc.invalidateQueries({ queryKey: ["users-needing-action"] });
    },
  });
};

/** Hard delete. Every child row cascades off the visit — there is no undo. */
export const useDeleteLabVisit = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await LabVisitsService.deleteApiLabVisitsById({
        path: { id },
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error("Kunne ikke slette labbesøket");
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-visits"] });
      qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] });
      qc.invalidateQueries({ queryKey: ["impersonated-lab-visits"] });
      qc.invalidateQueries({ queryKey: ["crm-users"] });
      qc.invalidateQueries({ queryKey: ["users-needing-action"] });
      qc.invalidateQueries({ queryKey: ["biometric-results"] });
      qc.invalidateQueries({ queryKey: ["impersonated-biometric-results"] });
    },
  });
};

/**
 * Requisitions the doctor has finalised ("Send forslag") and are awaiting
 * Furst POST. Drives the Rekvirering tab.
 *
 * `enabled` is how the page keeps this scan — and the four id-batched fan-outs
 * that hang off its results — off the four tabs that never render a
 * requisition. The dependent hooks need no gate of their own: with this
 * disabled their id lists are empty, which already disables them.
 */
export const useReadyForSubmissionRequisitions = (enabled = true) =>
  useQuery({
    queryKey: ["lab-visit-requisitions", "readyForSubmission"],
    queryFn: () =>
      fetchAll<LabVisitRequisition>((opts) =>
        LabVisitsService.getApiLabVisitRequisitions({
          ...opts,
          query: { ...opts.query, "filter[status]": "readyForSubmission" },
        }),
      ),
    enabled,
    staleTime: 30_000,
  });

/**
 * Start of the "Nylig rekvirert" window: midnight, 7 days back.
 *
 * Anchored to midnight rather than to the current instant because it goes in a
 * query key, and a value that moves every render would refetch forever.
 */
export const recentlySubmittedSince = () => {
  const since = new Date();
  since.setDate(since.getDate() - 7);
  since.setHours(0, 0, 0, 0);
  return since.toISOString();
};

/**
 * Requisitions POSTed to Furst within the last 7 days, regardless of whether
 * results have started arriving. Drives the "Nylig rekvirert" table.
 *
 * The window is applied by the API. It used to be a client-side `select` over
 * an unfiltered scan, so the page paginated through every requisition ever
 * submitted — a set that only grows — to keep a week of them.
 */
export const useRecentlySubmittedRequisitions = (enabled = true) => {
  const since = recentlySubmittedSince();
  return useQuery({
    queryKey: ["lab-visit-requisitions", "recentlySubmitted", since],
    queryFn: () =>
      fetchAll<LabVisitRequisition>((opts) =>
        LabVisitsService.getApiLabVisitRequisitions({
          ...opts,
          query: {
            ...opts.query,
            "filter[status]": "in:submitted,awaitingResults,allResultsReceived",
            "filter[submittedAt]": `gte:${since}`,
          },
        }),
      ),
    enabled,
    staleTime: 30_000,
  });
};

/**
 * Submitted requisitions with no time limit — used to surface snail-mail
 * lab visits still waiting on manual posting. The page filters down to
 * `crmUser.labSnailMailRequisition && labVisit.status === 'pendingRequisition'`.
 * No time slice here: these rows are blocking work and must stay visible
 * until an admin clicks "Sett rekvirert".
 */
export const useAwaitingSnailMailRequisitions = (enabled = true) =>
  useQuery({
    queryKey: ["lab-visit-requisitions", "awaitingSnailMail"],
    queryFn: () =>
      fetchAll<LabVisitRequisition>((opts) =>
        LabVisitsService.getApiLabVisitRequisitions({
          ...opts,
          query: { ...opts.query, "filter[status]": "submitted" },
        }),
      ),
    enabled,
    staleTime: 30_000,
  });

/**
 * Batch fetch biometricIds keyed by requisitionId. Batched by id for the same
 * reason as `useLabVisitsByIds` — the requisition set is unbounded, and one
 * `in:` filter holding all of it eventually exceeds the API's request-size
 * limit and takes every biomarker column on the page down with it.
 */
export const useRequisitionBiometricsMap = (requisitionIds: string[]) =>
  useQuery({
    queryKey: [
      "lab-visit-requisition-biometrics",
      "byReq",
      [...requisitionIds].sort(),
    ],
    queryFn: async () => {
      const result = new Map<string, string[]>();
      const rows = await fetchAllByIds<{
        requisitionId: string;
        biometricId: string;
      }>([...requisitionIds].sort(), (batch, opts) =>
        LabVisitsService.getApiLabVisitRequisitionBiometrics({
          ...opts,
          query: {
            ...opts.query,
            "filter[requisitionId]": `in:${batch.join(",")}`,
          },
        }),
      );
      for (const r of rows) {
        const list = result.get(r.requisitionId) ?? [];
        list.push(r.biometricId);
        result.set(r.requisitionId, list);
      }
      return result;
    },
    enabled: requisitionIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

/**
 * Requisitioned lab visits for a known set of users — the "Rekvirert" badge on
 * the user table. Scoped by user because the badge is only ever asked about
 * rows already on screen, while the unscoped version grew with every visit
 * that stalled in `requisitioned` instead of with the worklist.
 */
export const useRequisitionedForUsers = (authUserIds: string[]) => {
  const ids = [...new Set(authUserIds)].sort();
  return useQuery({
    queryKey: ["lab-visits", "requisitionedForUsers", ids],
    queryFn: () =>
      fetchAllByIds<LabVisit>(ids, (batch, opts) =>
        LabVisitsService.getApiLabVisits({
          ...opts,
          query: {
            ...opts.query,
            "filter[status]": "requisitioned",
            "filter[authUserId]": `in:${batch.join(",")}`,
          },
        }),
      ),
    enabled: ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
};

/**
 * Active B2B lab visits among a known set of visits — the "(B2B:BHT)" suffix on
 * the status badge. Scoped by visit id for the same reason as
 * `useRequisitionedForUsers`: the unscoped set held every B2B visit that never
 * reached assessmentCompleted, so it only ever grew.
 */
export const useB2bLabVisitsByIds = (labVisitIds: string[]) => {
  const ids = [...new Set(labVisitIds)].sort();
  return useQuery({
    queryKey: ["lab-visits", "b2bByIds", ids],
    queryFn: () =>
      fetchAllByIds<LabVisit>(ids, (batch, opts) =>
        LabVisitsService.getApiLabVisits({
          ...opts,
          query: {
            ...opts.query,
            "filter[id]": `in:${batch.join(",")}`,
            "filter[companyId]": "is_nil:false",
            "filter[status]": "ne:assessmentCompleted",
          },
        }),
      ),
    enabled: ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
};

/** Requisitioned lab visits from the last 30 days */
export const useRequisitionedForEtterrekvirering = () =>
  useQuery({
    queryKey: ["lab-visits", "recentlyRequisitioned"],
    queryFn: () =>
      fetchAll<LabVisit>((opts) =>
        LabVisitsService.getApiLabVisits({
          ...opts,
          query: { ...opts.query, "filter[status]": "requisitioned" },
        }),
      ),
    staleTime: 60_000,
    select: (data) => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      return data.filter((lv) => new Date(lv.updatedAt) >= cutoff);
    },
  });

export const useSendEtterrekvirering = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requisitionId,
      furstCookie,
      biometricIds,
    }: {
      requisitionId: string;
      furstCookie: string;
      biometricIds: string[];
    }) => {
      const res =
        await LabVisitsService.postApiAdminLabVisitRequisitionsByIdEtterrekvirering(
          {
            path: { id: requisitionId },
            body: { furstCookie, biometricIds },
            headers: getAuthHeaders(),
          },
        );
      if (res.error) {
        const err = res.error as { message?: string } | string;
        throw new Error(
          typeof err === "string"
            ? err
            : (err.message ?? "Etterrekvirering failed"),
        );
      }
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-visits"] });
      qc.invalidateQueries({ queryKey: ["users-needing-action"] });
      qc.invalidateQueries({ queryKey: ["lab-visit-requisition-biometrics"] });
      qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] });
    },
  });
};

/**
 * Directly sets a visit's status, bypassing the state machine and its
 * notifications. Named for what it does, not for the legacy route path: the
 * anamnesis page uses it to step a visit back, the CRM table uses it to
 * override any status. The backend writes only the row and its status history.
 */
export const useOverrideLabVisitStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: NonNullable<LabVisitUpdate["status"]>;
    }) => {
      const res = await LabVisitsService.postApiAdminLabVisitsByIdRevertStatus({
        path: { id },
        body: { status },
        headers: getAuthHeaders(),
      });
      if (res.error) {
        // The route's 422 body carries the lifecycle guard's explanation
        // (e.g. unsent requisitions before `requisitioned`). Surface it
        // instead of a generic message.
        const error = res.error as { message?: string } | string;
        throw new Error(
          typeof error === "string"
            ? error
            : (error.message ?? "Kunne ikke endre status"),
        );
      }
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-visits"] });
      qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] });
      qc.invalidateQueries({ queryKey: ["users-needing-action"] });
      // usedVisits on the CRM row counts only some statuses, so the row it was
      // overridden from has to refetch too.
      qc.invalidateQueries({ queryKey: ["crm-users"] });
    },
  });
};

export type MockLabResultsMode = "partial" | "all";

/** Mirrors the endpoint's 200 body — the generated response type widens to a
 *  union of its own field types, same as SyncLabVisitMarkersResult above. */
export type MockLabResultsResult = {
  status: "success";
  liaisonId: string;
  jobId: number;
  mode: MockLabResultsMode;
  /** content.status.value in the fake payload: "S" = delsvar, "F" = endelig. */
  contentStatus: "S" | "F";
  requisitions: Array<{
    requisitionId: string;
    helfo: boolean;
    proveId: string;
    deliveredBiometricIds: string[];
  }>;
  /** Requisitioned markers with no NLK code — Fürst could never report them. */
  skippedBiometricIds: string[];
};

/**
 * Dev/preview only — fakes an incoming Fürst/Liaison result payload for a lab
 * visit so the blood-test flow can be walked without a real lab. The endpoint
 * 404s unless the backend runs with ALLOW_MOCK_LAB_RESULTS=true, so callers
 * must gate the UI on isDevMode().
 *
 * The backend only enqueues process-liaison; results and statuses land a
 * second or two later, hence the delayed re-invalidations.
 */
export const useMockLabResults = () => {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["lab-visits"] });
    qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] });
    qc.invalidateQueries({ queryKey: ["users-needing-action"] });
    // The anamnesis visit page reads the patient through impersonation.
    qc.invalidateQueries({ queryKey: ["impersonated-lab-visits"] });
    qc.invalidateQueries({ queryKey: ["impersonated-biometric-results"] });
  };
  return useMutation({
    mutationFn: async ({
      labVisitId,
      mode,
    }: {
      labVisitId: string;
      mode: MockLabResultsMode;
    }): Promise<MockLabResultsResult> => {
      const res =
        await LabVisitsService.postApiAdminLabVisitsByIdMockLabResults({
          path: { id: labVisitId },
          body: { mode },
          headers: getAuthHeaders(),
        });
      if (res.error) {
        const err = res.error as { message?: string } | string;
        throw new Error(
          typeof err === "string"
            ? err
            : (err.message ?? "Kunne ikke sende lab-svar"),
        );
      }
      return res.data as unknown as MockLabResultsResult;
    },
    onSuccess: () => {
      invalidate();
      setTimeout(invalidate, 1500);
      setTimeout(invalidate, 4000);
    },
  });
};

/**
 * Per-send correction to the patient address FURST returns from its own
 * folkeregister lookup. Nothing is persisted — it applies to this send only.
 */
export interface FurstAddressOverride {
  adresse?: string;
  postnummer?: string;
  poststed?: string;
}

/**
 * POST one lab visit's ready requisitions to FURST.
 *
 * Deliberately invalidates nothing. Its only caller sends a queue of visits in
 * a loop with the rekvirering page mounted behind the modal, and between them
 * the `lab-visits` and `lab-visit-requisitions` keys cover five unbounded scans
 * on that page — readyForSubmission, recentlySubmitted and awaitingSnailMail
 * requisitions, plus the allRequisitioned and activeB2b visit lists — feeding
 * six id-batched fan-outs. Refreshing per send re-ran all of it once per visit
 * — and cold, because a send moves a requisition between the scans whose ids
 * make up the fan-outs' query keys, so nothing is served from cache. A queue of
 * 20 therefore cost 20 page loads and put the office past the edge's per-IP
 * rate limit (1200/min, shared behind the NAT — see hooks/api/fetchAll.ts).
 * The caller refreshes once when the queue is done, and only if a send landed.
 */
export const useSendRequisition = () =>
  useMutation({
    retry: false,
    mutationFn: async ({
      id,
      furstCookie,
      addressOverride,
    }: {
      id: string;
      furstCookie: string;
      addressOverride?: FurstAddressOverride;
    }) => {
      const res = await LabVisitsService.postApiAdminLabVisitsByIdRequisition({
        path: { id },
        body: { furstCookie, addressOverride },
        headers: getAuthHeaders(),
      });
      if (res.error) {
        // The route returns a JSON object `{ status, message }`, so the
        // string branch never fires — without unwrapping `message` we'd
        // throw away FURST's explanation of what it rejected.
        const err = res.error as { message?: string } | string;
        throw new Error(
          typeof err === "string" ? err : (err.message ?? "Requisition failed"),
        );
      }
      return res.data;
    },
  });

/** A doctor has checked Fürst: the attempt is not there, so clear it. */
export const useConfirmFurstNotReceived = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, attemptId }: { id: string; attemptId: string }) => {
      const res =
        await LabVisitsService.postApiAdminLabVisitsByIdRequisitionFurstNotReceived({
          path: { id },
          body: { attemptId },
          headers: getAuthHeaders(),
        });
      if (res.error) {
        const err = res.error as { message?: string };
        throw new Error(err.message ?? "Kunne ikke bekrefte");
      }
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["lab-visits"] }),
        qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] }),
      ]),
  });
};

export const useLabVisitRequisitions = (labVisitId: string | undefined) =>
  useQuery({
    queryKey: ["lab-visit-requisitions", labVisitId],
    queryFn: async () => {
      const res = await LabVisitsService.getApiLabVisitRequisitions({
        query: { "filter[labVisitId]": labVisitId!, limit: 10 },
        headers: getAuthHeaders(),
      });
      return res.data ? (res.data as LabVisitRequisition[]) : [];
    },
    enabled: !!labVisitId,
    staleTime: 30_000,
  });

/** Biometrics on a single requisition (etter-rekvirering modal). */
export const useRequisitionBiometricsList = (
  requisitionId: string | undefined,
) =>
  useQuery({
    queryKey: ["lab-visit-requisition-biometrics", "single", requisitionId],
    queryFn: async () => {
      const res = await LabVisitsService.getApiLabVisitRequisitionBiometrics({
        query: { "filter[requisitionId]": requisitionId!, limit: 200 },
        headers: getAuthHeaders(),
      });
      return ((res.data as any) ??
        []) as unknown as LabVisitRequisitionBiometric[];
    },
    enabled: !!requisitionId,
    staleTime: 30_000,
  });

export const useLabVisitRequisitionBiometrics = (
  labVisitId: string | undefined,
) =>
  useQuery({
    queryKey: ["lab-visit-requisition-biometrics", labVisitId],
    queryFn: async () => {
      const reqRes = await LabVisitsService.getApiLabVisitRequisitions({
        query: {
          "filter[labVisitId]": labVisitId!,
          "filter[status]": "ne:cancelled",
          limit: 10,
        } as any,
        headers: getAuthHeaders(),
      });
      const requisitions: { id: string }[] = (reqRes.data as any) ?? [];
      if (requisitions.length === 0) return [];
      const pages = await Promise.all(
        requisitions.map((r) =>
          LabVisitsService.getApiLabVisitRequisitionBiometrics({
            query: { "filter[requisitionId]": r.id, limit: 200 },
            headers: getAuthHeaders(),
          }).then((res) => (res.data as any) ?? []),
        ),
      );
      return pages.flat() as unknown as LabVisitRequisitionBiometric[];
    },
    enabled: !!labVisitId,
    staleTime: 0,
  });

export type SyncLabVisitMarkersResult = {
  status: "success";
  helfoRequisitionId: string | null;
  nonHelfoRequisitionId: string | null;
  helfoBiometricIds: string[];
  nonHelfoBiometricIds: string[];
  helfoReasons: Record<string, string>;
  helfoClinicalInfo: string | null;
};

export const useSyncLabVisitMarkers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      labVisitId,
      helfoBiometricIds,
      nonHelfoBiometricIds,
      clinicalInfo,
      helfoReasons,
    }: {
      labVisitId: string;
      helfoBiometricIds?: string[];
      nonHelfoBiometricIds?: string[];
      clinicalInfo?: string | null;
      helfoReasons?: Record<string, string>;
    }): Promise<SyncLabVisitMarkersResult> => {
      const res =
        await LabVisitsService.putApiAdminLabVisitsByLabVisitIdMarkers({
          path: { labVisitId },
          body: {
            helfoBiometricIds,
            nonHelfoBiometricIds,
            clinicalInfo,
            helfoReasons,
          },
          headers: getAuthHeaders(),
        });

      if (res.error) throw new Error("Failed to sync markers");
      return res.data as unknown as SyncLabVisitMarkersResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lab-visit-requisitions"] });
      qc.invalidateQueries({ queryKey: ["lab-visit-requisition-biometrics"] });
      qc.invalidateQueries({ queryKey: ["lab-visits"] });
    },
  });
};
